/**
 *
 * Audiences
 *
 */

import React, { memo, useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import { createStructuredSelector } from "reselect";
import { compose } from "redux";
import { Link, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

import { useInjectSaga } from "utils/injectSaga";
import { useInjectReducer } from "utils/injectReducer";
import reducer from "./reducer";
import saga from "./saga";

import {
  flushState,
  setInitialID,
  loadAudienceDetail,
  changeAudience,
  hardDeleteAudience,
} from "./actions";

import {
  // init
  makeSelectFirstRender,
  makeSelectInitialID,
  // load
  makeSelectAudienceDetail,
  makeSelectAudienceLoading,
  makeSelectAudienceError,
  // new
  makeSelectAudienceIsNew,
  makeSelectAudienceIsNewSuccess,
  // update
  makeSelectAudienceUpdateSuccess,
  makeSelectAudienceUpdateLoading,
  makeSelectAudienceUpdateError,
  // delete
  makeSelectAudienceDeleteSuccess,
  makeSelectAudienceDeleteLoading,
  makeSelectAudienceDeleteError,
} from "./selectors";

// antd component
import { Layout, Form, Popconfirm } from "antd";
const { Content } = Layout;

// assets
import { DeleteOutlined } from "@ant-design/icons";
//import CustomIcons from 'components/CustomIcons';

import ErrorBoundary from "engine/ErrorBoundary";

// components
import CustomTable from "components/CustomTable";
import TCFModal from "components/TCFModal";
import {
  NotificationHandleNew,
  NotificationHandleUpdate,
  NotificationSimple,
} from "components/Notifications";
import SelectFieldAxios from "containers/SelectFieldAxios";
import { AudienceHeader } from "components/Audiences";

// helper for forms
import { useRefState } from "utils/hooks";
import { USER_TYPES_OPTIONS } from "utils/helpers/helper.constant";
import { parseQuery, encodeQueryData } from "utils/history";

export function AudienceDetail({
  // main data
  itemDetail,
  // states
  initialID,
  firstRender,
  history,
  // load item
  loadDetail,
  errorDetail,
  // new
  isNew,
  isNewSuccessful,
  // update
  updateSuccessful,
  loadingUpdate,
  // delete
  hardDeleteSuccessful,
  hardDeleteLoading,
  // actions
  loadAudience,
  changeAudience,
  hardDelete,
  flushState,
  setInitialID,
  //default
  className,
}) {
  useInjectReducer({ key: "AudienceDetail", reducer });
  useInjectSaga({ key: "AudienceDetail", saga });

  const { t, i18n } = useTranslation();

  const newKey = "new";
  const location = useLocation();
  const { id: initId } = useParams();

  const [currentElement, currentElementRef, setCurrentElement] = useRefState(
    itemDetail || null,
  );

  const [tableRows, setTableRow] = useState([]);
  const [newElement, setNewElement] = useState({});
  const [currentType, setCurrentType] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showError, setShowError] = useState(false);

  // Form initialization for new row
  const [addNewFrom] = Form.useForm();

  const deleteCallback = useCallback(id => hardDelete(id || initId), [initId]);

  const goToCallback = useCallback(() => {
    const searchParams = parseQuery(location.search) || null;
    let currentData = searchParams;
    if (searchParams.hasOwnProperty("preview")) {
      delete currentData["preview"];
    }
    const { sortBy, ...searchData } = currentData;
    const serachStr = encodeQueryData(searchData);
    history.push({
      pathname: `/audiences`,
      search: `?${serachStr}`,
      state: {
        from: "audience_detail",
        preview: false,
        params: currentData,
      },
    });
  }, [history, location.search]);

  const onSave = useCallback(() => {
    if (JSON.stringify(currentElement) != JSON.stringify(itemDetail)) {
      changeAudience(currentElement);
    }
  }, [currentElement, tableRows]);

  // return: init, members
  const prepearInitAndMembers = useCallback(initElement => {
    let currEl = {
      init: initElement || {},
      members: [],
    };
    if (!!initElement) {
      let students = [];
      let users = [];
      if (initElement?.students && initElement?.students?.length > 0) {
        students = initElement?.students
          ?.map(elem => {
            return {
              ...elem,
              type: USER_TYPES_OPTIONS("student"),
              typeInit: "students",
              firstname: elem?.description?.firstname,
              lastname: elem?.description?.lastname,
            };
          })
          .sort((a, b) => a?.lastname?.localeCompare(b?.lastname));
      }

      if (initElement?.users && initElement?.users?.length > 0) {
        users = initElement?.users
          ?.map(elem => {
            return {
              ...elem,
              type: USER_TYPES_OPTIONS(elem?.role || "admin"),
              typeInit: "users",
              firstname: elem?.description?.firstname,
              lastname: elem?.description?.lastname,
            };
          })
          .sort((a, b) => a?.lastname?.localeCompare(b?.lastname));
      }

      currEl = {
        ...currEl,
        init: {
          ...initElement,
          students: students,
          users: users,
        },
        members: [...students, ...users],
      };
    }
    return currEl;
  }, []);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (initId) {
      setInitialID(initId);
      initId != newKey && loadAudience(initId);
      // open new form
      if (initId == newKey) {
        //console.log('create new');
      }
    }

    return () => {
      // Anything in here is fired on component unmount.
      // Clearing the state after unmounting a component
      flushState();
    };
  }, []);

  // Because history not trigger update component
  useEffect(() => {
    setInitialID(initId);
    setCurrentElement(false);
    if (initId && initId !== newKey) {
      loadAudience(initId);
    }
  }, [initId]);

  useEffect(() => {
    if (!!itemDetail) {
      const { init, members } = prepearInitAndMembers(itemDetail);
      setTableRow(members || []);
      setCurrentElement(init || itemDetail);
    }
  }, [itemDetail, i18n?.language]);

  useEffect(() => {
    if (!!hardDeleteSuccessful) {
      goToCallback();
    }
  }, [hardDeleteSuccessful]);

  // create new Row with form
  const makeNewRow = useMemo(() => {
    // initial data for SelectFieldAxios component
    let newElementSelect = {
      name: "new_element",
      endponitSend: {},
      required: true,
      displayLabel: false,
      value: "",
      type: "axios-select",
      col: 24,
      itemCallback: item => {
        return {
          key: item?._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item?._id,
          item: item,
        };
      },
    };

    if (currentType == "admin" || currentType == "teacher") {
      newElementSelect = {
        ...newElementSelect,
        label: `${t("common.new")} ${currentType}`,
        name: "new_element",
        endponit: "users/role",
        endponitSend: {
          role: currentType,
        },
        onSelect: (LabeledValue, option) => {
          setNewElement({
            ...option?.item,
            type: USER_TYPES_OPTIONS(currentType),
            typeInit: "users",
            firstname: option?.item?.description?.firstname,
            lastname: option?.item?.description?.lastname,
          });
        },
      };
    } else {
      newElementSelect = {
        ...newElementSelect,
        label: `${t("common.new")} ${t("labels.student")}`,
        endponit: "students/list",
        endponitSend: {},
        onSelect: (LabeledValue, option) => {
          setNewElement({
            ...option?.item,
            type: USER_TYPES_OPTIONS(currentType),
            typeInit: "students",
            firstname: option?.item?.description?.firstname,
            lastname: option?.item?.description?.lastname,
          });
        },
      };
    }

    return (
      <Form form={addNewFrom} name="add-new-element" autoComplete="off">
        <SelectFieldAxios
          className="table-select"
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          itemCallback={newElementSelect.itemCallback}
          onSelect={newElementSelect.onSelect}
          item={newElementSelect}
        />
        {showError && (
          <span className="alert">{t("common.existInList")}</span>
        )}
      </Form>
    );
  }, [currentType, addNewFrom, showError, setNewElement, i18n?.language]);

  // Remove and update current Element
  const removeElement = elementObj => {
    if (elementObj && elementObj?._id) {
      const typeInit = elementObj?.typeInit || "students";
      let elements = currentElement[typeInit] || [];
      const newElements =
        elements?.filter(el => el?._id !== elementObj?._id) || [];
      const newElem = {
        ...currentElement,
        [typeInit]: newElements,
      };
      const { init, members } = prepearInitAndMembers(newElem);
      setTableRow(members || []);
      setCurrentElement(init || itemDetail);
    }
  };

  const onCancelModalHandler = useCallback(() => {
    addNewFrom.resetFields();
    setCurrentType("");
    setShowError(false);
    setNewElement({});
  }, []);

  // save/add new Element form new Row
  const onAddElementHandler = useCallback(() => {
    addNewFrom.submit();
    const val = addNewFrom.getFieldsValue();

    let typeInit = "users";
    if (currentType == "student") {
      typeInit = "students";
    }

    if (val?.new_element && newElement) {
      const elements = currentElement[typeInit] || [];
      const hasElement = elements.some(
        element => element?._id === newElement?._id,
      );

      if (!hasElement) {
        setShowError(false);
        const newEl = {
          _id: newElement?._id,
          description: newElement?.description,
        };
        const newEls = [newEl, ...elements];
        const newElem = {
          ...currentElement,
          [typeInit]: newEls,
        };
        const { init, members } = prepearInitAndMembers(newElem);
        setTableRow(members || []);
        setCurrentElement(init || itemDetail);
        setShowModal(false);
      } else {
        setShowError(true);
      }
    }
  }, [currentType, addNewFrom, currentElement, newElement]);

  const actionsColumn = {
    title: t("common.action"),
    dataIndex: "action",
    key: "action-column-student",
    className: "column-actions",
    render: (_, record) => {
      return (
        <Popconfirm
          placement="top"
          title={t("common.removalRequest")}
          okText={t("common.yes")}
          cancelText={t("common.no")}
          onConfirm={() => removeElement(record)}
          overlayClassName="popconfirm-window"
        >
          <DeleteOutlined className="delete-action" />
        </Popconfirm>
      );
    },
  };

  const columns = [
    {
      title: t("common.lastNameShort"),
      dataIndex: "lastname",
      key: "column-lastname",
      className: "column-lastname",
      sorter: (a, b) => a?.lastname?.localeCompare(b?.lastname),
      render: (text, record) => record?.lastname,
    },
    {
      title: t("common.firstName"),
      dataIndex: "firstname",
      key: "column-firstname",
      className: "column-firstname",
      sorter: (a, b) => a?.firstname?.localeCompare(b?.firstname),
      render: (text, record, index) => record?.firstname,
    },
    {
      title: t("labels.type"),
      dataIndex: "type",
      key: "column-type",
      className: "column-type",
      sorter: (a, b) => a?.type?.localeCompare(b?.type),
      render: (text, record, index) => record?.type,
    },
    {
      title: t("labels.class"),
      dataIndex: "className",
      key: "column-class",
      className: "column-class",
      sorter: (a, b) =>
        a?.class_info?.title?.localeCompare(b?.class_info?.title),
      render: (text, record, index) => {
        return record?.class_info?._id ? (
          <Link
            className="link-table"
            to={`/classes/${record?.class_info?._id}`}
          >
            {record?.class_info?.title}
          </Link>
        ) : (
          ""
        );
      },
    },
  ];

  // Set action column object
  columns.push(actionsColumn);

  const onAddNewMemberHandler = useCallback(memberType => {
    onCancelModalHandler();
    setCurrentType(memberType);
    setShowModal(true);
  }, []);

  // render function
  return (
    <Layout className="main-full-content">
      <Helmet>
        <title>{t("labels.audiences")}</title>
        <meta name="description" content="Description of Audiences" />
      </Helmet>

      <ErrorBoundary>
        <NotificationHandleUpdate
          Loading={loadingUpdate}
          isNew={isNew}
          Successful={updateSuccessful}
        />
        <NotificationHandleNew
          Loading={loadingUpdate}
          isNew={isNew}
          Successful={isNewSuccessful}
        />
        <NotificationSimple
          Loading={hardDeleteLoading}
          Successful={hardDeleteSuccessful}
        />

        <TCFModal
          open={showModal}
          setOpen={setShowModal}
          title={t("audiences.addNewMember")}
          loading={loadingUpdate}
          confirmLoading={updateSuccessful}
          onCancel={onCancelModalHandler}
          onOk={onAddElementHandler}
        >
          {makeNewRow}
        </TCFModal>

        <Content className="main-content-container">
          <div className="container-wrapped">
            <AudienceHeader
              currentElement={currentElement}
              loadingUpdate={loadingUpdate}
              loadingDelete={hardDeleteLoading}
              updateElement={setCurrentElement}
              onSave={onSave}
              onDelete={deleteCallback}
              onAddNewMember={onAddNewMemberHandler}
              onGoBack={goToCallback}
            />

            <CustomTable
              className="audiences-table"
              dataSource={tableRows || []}
              columns={columns}
              loading={loadDetail}
            />
          </div>
        </Content>
      </ErrorBoundary>
    </Layout>
  );
}

AudienceDetail.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loadDetail: PropTypes.bool,
  loadDetail: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
  initialID: makeSelectInitialID(),
  firstRender: makeSelectFirstRender(),
  // load
  itemDetail: makeSelectAudienceDetail(),
  loadDetail: makeSelectAudienceLoading(),
  errorDetail: makeSelectAudienceError(),
  // new
  isNew: makeSelectAudienceIsNew(),
  isNewSuccessful: makeSelectAudienceIsNewSuccess(),
  // update
  updateSuccessful: makeSelectAudienceUpdateSuccess(),
  loadingUpdate: makeSelectAudienceUpdateLoading(),
  errorUpdate: makeSelectAudienceUpdateError(),
  // delete
  hardDeleteSuccessful: makeSelectAudienceDeleteSuccess(),
  hardDeleteLoading: makeSelectAudienceDeleteLoading(),
  hardDeleteError: makeSelectAudienceDeleteError(),
});

function mapDispatchToProps(dispatch) {
  return {
    flushState: () => dispatch(flushState()),
    setInitialID: id => dispatch(setInitialID(id)),
    loadAudience: detailID => dispatch(loadAudienceDetail(detailID)),
    changeAudience: (itemDetail, action) =>
      dispatch(changeAudience(itemDetail, action)),
    hardDelete: hardDeleteId => dispatch(hardDeleteAudience(hardDeleteId)),
    dispatch,
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AudienceDetail);
